import axios from 'axios';

import { storage } from '@utils/browser';

import { config } from 'app-config';

import { isInTemplateRoute } from 'ui/router';

import { queryClient } from './query-client';

const instance = axios.create({
  baseURL: config.API_URL,
  xsrfHeaderName: 'X-CSRFToken',
  xsrfCookieName: 'csrftoken',
});

const cancelTokenSource = axios.CancelToken.source();

const getCSRFToken = async (): Promise<string> => {
  const response = await instance.get('v1/auth/csrf');
  return response.headers?.['x-csrftoken'] ?? '';
};

instance.interceptors.request.use(async (config) => {
  const debugToken = storage.get('dbg');
  const isAwsRequest = config.url?.includes('amazonaws.com');

  if (isInTemplateRoute()) {
    const regex = /^v[0-9]\/templates\//;
    const url = config.url ?? '';
    if (
      !regex.test(url) &&
      !config.url?.startsWith('v1/auth') &&
      !config.url?.startsWith('v1/workspaces')
    ) {
      config.cancelToken = cancelTokenSource.token;
      cancelTokenSource.cancel('Non-template related request cancelled');
    }
  }

  if (isAwsRequest) {
    // We don't want to set credentials: include for AWS blob requests
    // as it will throw CORS errors
    config.withCredentials = false;
  } else if (debugToken) {
    config.headers = { Authorization: `Token ${debugToken}`, ...config.headers };
    config.withCredentials = false;
  } else {
    config.withCredentials = true;
    if (config.method?.toLowerCase() !== 'get') {
      const CSRFToken = await getCSRFToken();
      config.headers = { 'X-CSRFToken': CSRFToken, ...config.headers };
    }
  }

  if (config.responseType === 'blob') {
    config.transformRequest = [
      (data, headers) => {
        headers.common?.delete('Content-Type');
        return data;
      },
    ];
  }

  return config;
});

instance.interceptors.response.use((response) => {
  if (response.status === 401) {
    removeDebugToken();
    queryClient.resetQueries();
  }
  return response;
});

export const request = instance;

export const setDebugToken = (token: string) => {
  storage.set('dbg', token);
};

export const removeDebugToken = () => {
  storage.remove('dbg');
};

export const loadBlob = async (url: string) => {
  const response = await request.get(url, { responseType: 'blob' });
  return response.data;
};
